import { useEffect, useMemo, useRef, useState } from "react";
import { api, ApiError } from "../api/client";
import { useToast } from "../components/Toast";
import InfoTip from "../components/InfoTip";
import { BOOK_LABELS, type ArCustomer, type Book, type CarriedSheet, type InsurerInfo, type ManualMatchPair, type MultiPreviewEntry, type ReconcileResponse, type ReconSettings, type RollforwardResponse } from "../types/api";
import { DEFAULT_SIDEBAR, DEFAULT_USD_RATE, DEFAULT_EUR_RATE, type SidebarSettings } from "../types/ui";
import FilePicker from "../components/FilePicker";
import FilePreviewCard from "../components/FilePreviewCard";
import InsurerCombobox from "../components/InsurerCombobox";
import ReconMonthDialog, { monthEndISO } from "../components/ReconMonthDialog";
import Sidebar from "../components/Sidebar";
import CustomerVarianceStep, { previousMonth } from "../components/CustomerVarianceStep";
import RollforwardPage from "./RollforwardPage";

export interface ReconInputs {
  // A LIST: CRI books some insurers under several INSURANCE COMPANY codes
  // (Watania: TAKAFUL GENERAL + TAKAFUL FAMILY) that reconcile as one account.
  // EMPTY only on a session restored after a refresh: File objects can't be
  // serialised, so the re-run paths that need the raw bytes are disabled until the
  // user re-uploads (see ResultPage.filesLost).
  criFiles: File[];
  insurerFiles: File[];
  insurerName: string;
  insurerSheetName: string;
  // Which reconciliation book this run belongs to. Part of the run's IDENTITY, not a
  // display flag: every re-run off these inputs must go back to the same book, and the
  // restored-session record keys off it so a refresh cannot relabel one book's figures
  // with the other's. Absent = AP, the same rule the server applies.
  book?: Book;
  settings: ReconSettings;
  insurerSheetFilter?: [string, string][];
  // The prior-period unreconciled file (GL, optional). Carried here because EVERY
  // re-run must re-send it: /api/reconcile derives the whole prior-period block from
  // this upload, so a re-run without it returns unreconciled=null and the block
  // silently disappears — tab, KPI cards and Excel sheet all gone. That is exactly
  // what happened when a carried manual match triggered its re-run.
  unreconciledFile?: File | null;
  // The sheet pinned for each carried file ("" = the engine's most-items scan). Carried
  // for the same reason as the files: a re-run that drops the pin falls back to the scan
  // and can read the other book's tab off finance's two-sided workbook.
  unreconciledSheetName?: string;
  // The CRI-side prior-period file (GL, optional), carried and re-sent for exactly the
  // same reason as the insurer-side one above.
  unreconciledCriFile?: File | null;
  unreconciledCriSheetName?: string;
  // AR only: the customer the user picked. Re-sent on every re-run for the same reason
  // as the book: without it the server reads the file's own customer and its store.
  customerKey?: string;
  customerName?: string;
  // Set only on a session restored after a refresh: the hand-made edits the restored
  // result was finalized with. The dashboard must start from these, or its first save
  // overwrites the stored copy with empty lists and the next export drops the notes.
  restored?: {
    sessionPairs: ManualMatchPair[];
    notesCri: Record<string, string>;
    notesInsurer: Record<string, string>;
    bridgeRejections: string[];
  };
}

interface Props {
  settings: SidebarSettings;
  onSettingsChange: (s: SidebarSettings) => void;
  // The (insurer, CRI-span) the period defaults were last seeded for, owned by App so
  // it SURVIVES this page unmounting on Re-upload. Held here it was a ref, which died
  // with the component and let the seed re-fire over the user's own window.
  periodSeedKey: string;
  onPeriodSeeded: (key: string) => void;
  // Rates in force from the user's saved set; passed to the sidebar for its notice,
  // along with the session-scoped acknowledgement of that notice.
  savedRates?: { usd?: number; eur?: number };
  rateNoticeAck?: string;
  onAckRateNotice?: (sig: string) => void;
  sidebarCollapsed: boolean;
  onToggleSidebar: () => void;
  onReconciled: (resp: ReconcileResponse, inputs: ReconInputs) => void;
  // Whether this user may reconcile a new (uncached) insurer, which triggers Gemini
  // detection. Off for most users; the new-insurer path is blocked when false.
  canUseGemini: boolean;
  // The reconciliation book this form is working in. Selects which insurers are offered
  // (each book has its own layouts) and rides along on every run.
  book: Book;
  // The signed-in username. Scopes the remembered last insurer, so the next person on a
  // shared browser is not handed the previous user's insurer.
  user: string;
}

const NO_SHEET = "(select a sheet)";

/** One ledger's prior-period open-items picker, shown inside that ledger's own card.
 *
 * Finance thinks in terms of "the CRI file and its open items" / "the SOA and its open
 * items", so the upload belongs beside the ledger it describes rather than in a separate
 * card further down. `disabled` is set when a previous-output workbook is supplied: that
 * file already carries BOTH books, and sending a per-ledger list as well would state the
 * same population twice. GL only — the caller gates on the basis. */
function PriorPeriodPicker({ side, file, onPick, onPreviousOutput, disabled, insurerName,
                             sheet, onSheet }: {
  side: "CRI" | "Insurer";
  file: File | null;
  onPick: (f: File | null) => void;
  // The attached file is last month's output: it belongs in the previous-output slot,
  // which reads both books off its group tabs (the CRI side alone cannot read it).
  onPreviousOutput: (f: File) => void;
  disabled: boolean;
  // Needed to read an insurer-side workbook through that insurer's own layout; the
  // CRI side is layout-independent and ignores it.
  insurerName: string;
  // "" = let the engine scan and keep the sheet with the most items.
  sheet: string;
  onSheet: (s: string) => void;
}) {
  const what = side === "CRI" ? "CRI" : "insurer";
  const [sheets, setSheets] = useState<CarriedSheet[]>([]);
  const [scanning, setScanning] = useState(false);
  const [scanErr, setScanErr] = useState("");

  // Detect on attach, exactly as the CRI/SOA pickers do. The scan runs the engine's
  // own readers, so the counts offered here are the counts the run will carry.
  useEffect(() => {
    if (!file) { setSheets([]); setScanErr(""); onSheet(""); return; }
    let live = true;
    setScanning(true); setScanErr(""); setSheets([]);
    api.detectCarriedSheets(side === "CRI" ? "cri" : "insurer", insurerName, file)
      .then((r) => {
        if (!live) return;
        if (r.previous_output) { onPick(null); onPreviousOutput(file); return; }
        const found = r.sheets ?? [];
        setSheets(found);
        // Pre-select the readable sheet when exactly ONE is readable — the two-sided
        // workbook case, where the other tab is the other book's list. With several
        // readable the user must choose, so nothing is pinned and the engine's own
        // most-items scan stands as the default.
        const usable = found.filter((s) => s.ok);
        onSheet(usable.length === 1 ? usable[0].sheet : "");
      })
      .catch((e) => { if (live) setScanErr(e instanceof Error ? e.message : String(e)); })
      .finally(() => { if (live) setScanning(false); });
    return () => { live = false; };
    // onSheet, onPick and onPreviousOutput are stable setters; re-running on them would loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file, side, insurerName]);

  const usableCount = sheets.filter((s) => s.ok).length;
  return (
    <div className="prior-period">
      <label className="field-label section-label" style={{ marginTop: 0 }}>
        Unreconciled items from earlier periods
        <span className="badge badge-inline badge-optional">Optional</span>
        <InfoTip text={`Items on this ${what} ledger that were still unreconciled at the end of the previous period. They are listed under Groups 1-3 and drive an Adjusted Closing Balance; your Closing Balance and Difference are not changed. Only needed for a FIRST reconciliation — after that, upload the previous month's output file below instead, which carries both ledgers at once. If finance sends ONE workbook holding both books, attach it here and on the other ledger too, then pick the matching sheet on each.`} />
      </label>
      {disabled ? (
        <p className="muted" style={{ margin: "4px 0 0" }}>
          Taken from the previous month&rsquo;s output file below.
        </p>
      ) : (
        <>
          <FilePicker
            accept=".xlsx,.xls,.xlsb,.csv"
            label={file ? `Replace ${what} open-items file…`
                        : `Choose ${what} open-items file (optional)`}
            onFiles={(f) => onPick(f[0] ?? null)}
          />
          {file && (
            <p className="muted note-lg">
              <strong>{file.name}</strong>
              {" "}
              <button className="btn" style={{ fontSize: "0.75rem", padding: "2px 8px", marginLeft: 6 }}
                onClick={() => onPick(null)}>Remove</button>
            </p>
          )}
          {file && scanning && <p className="muted note">Checking sheets…</p>}
          {file && scanErr && (
            <p className="muted note">Could not check this file&rsquo;s sheets. The run will
              scan them itself.</p>
          )}
          {file && !scanning && sheets.length > 0 && (
            <>
              <label className="field-label" htmlFor={`carried-sheet-${side}`}>
                Sheet to use
              </label>
              <select
                id={`carried-sheet-${side}`}
                className="select"
                value={sheet}
                onChange={(e) => onSheet(e.target.value)}
              >
                {/* Always offered: it is the engine's pre-existing behaviour and the
                    only sane choice when several sheets are readable. */}
                <option value="">Automatic (most items)</option>
                {sheets.map((s) => (
                  <option key={s.sheet} value={s.sheet} disabled={!s.ok}>
                    {s.ok ? `${s.sheet} — ${s.items} item${s.items === 1 ? "" : "s"}`
                          : `${s.sheet} — not readable on the ${what} side`}
                  </option>
                ))}
              </select>
              {usableCount === 0 ? (
                // Said plainly rather than left to the run: the whole prior-period
                // block is gated on this file parsing, so a silent miss loses the tab,
                // the KPI cards and the Excel sheet at once.
                // The parser's reason names schema columns; it stays reachable for
                // whoever debugs the layout, but is not the message a finance user reads.
                <div className="muted note">
                  No sheet in this workbook can be read as {what} items. Check it is
                  the {what} ledger&rsquo;s own open-items list, with its column headings
                  on one row.
                  {sheets[0]?.reason && (
                    <details style={{ marginTop: 4 }}>
                      <summary>Technical detail</summary>
                      {sheets[0].reason}
                    </details>
                  )}
                </div>
              ) : sheet ? (
                <p className="muted note">
                  ✓ Auto-detected sheet <strong>{sheet}</strong>
                  {" "}({sheets.find((s) => s.sheet === sheet)?.items ?? 0} items).
                </p>
              ) : (
                <p className="muted note">
                  {usableCount} readable sheets — pick one, or leave Automatic to take
                  the largest.
                </p>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}
// U+001F (unit separator), written as an ESCAPE so the source stays plain ASCII.
// A literal NUL byte here made git treat this whole file as BINARY, so every diff
// on it was unreviewable. The character never leaves the component - it only joins
// (file, sheet) into a Set key; buildSheetFilter sends the original pair.
const mergeKey = (fname: string, sheet: string) => `${fname}\u001f${sheet}`;

// Screen 1 — upload. Mirrors app/upload.py::_render_upload_screen: title + caption,
// two columns (CRI Source File | Insurer SOA with Step 1 insurer pick incl. the
// "Other (type manually)…" free-text path for NEW insurers, Step 2 the uploader —
// multi-file/zip with a "sheets to merge" checklist for multi-file insurers, single
// file + sheet dropdown otherwise), preview cards, the OS/GL basis radio defaulting
// to the detected basis, and the Run gate with its "Waiting for:" hints.
// The customer picker's "New customer…" entry; never a real key (keys are A-Z0-9._-).
const NEW_CUSTOMER = "__new__";

export default function UploadPage({ settings, onSettingsChange, periodSeedKey, onPeriodSeeded, savedRates, rateNoticeAck, onAckRateNotice, sidebarCollapsed, onToggleSidebar, onReconciled, canUseGemini, book, user }: Props) {
  const toast = useToast();
  const lastInsurerKey = `cri_last_insurer:${user}`;
  const [insurers, setInsurers] = useState<InsurerInfo[]>([]);
  const [pick, setPick] = useState("");            // supported name | new-insurer name | ""

  const [criFiles, setCriFiles] = useState<File[]>([]);
  // The FIRST CRI workbook drives the sheet detect, the sheet dropdown and the
  // span prefill: every CRI export is the same INSURANCE_COMPANY shape, so one
  // sheet name governs the whole upload (parse_cri_os_multi parses each file with
  // it). The rest of the list only ever adds rows.
  const criFile = criFiles[0] ?? null;
  const [criSheet, setCriSheet] = useState("");
  const [criSheets, setCriSheets] = useState<string[]>([]);
  const [criDetectedSheet, setCriDetectedSheet] = useState("");
  // The chosen CRI ledger's own first/last DOC DATE, used to prefill the period
  // filter for GL insurers that declare no explicit default.
  const [criSpan, setCriSpan] = useState<{ first: string | null; last: string | null }>(
    { first: null, last: null });
  // The insurer statement's own doc-date span. The as-of cutoff applies to BOTH books,
  // so the Run dialog needs this to say which side (if either) actually loses rows.
  const [insSpan, setInsSpan] = useState<{ first: string | null; last: string | null }>(
    { first: null, last: null });
  const [criErr, setCriErr] = useState("");
  const [mode, setMode] = useState("");      // detected basis
  const [modePick, setModePick] = useState("OS"); // governs the run (seeded from the insurer)
  // Has the user touched the basis radio? Once true, nothing re-seeds it: switching
  // insurer or re-uploading must never silently undo an explicit override.
  const [modeTouched, setModeTouched] = useState(false);
  function pickMode(val: string) { setModeTouched(true); setModePick(val); }

  // Single-file insurer path.
  const [insurerFile, setInsurerFile] = useState<File | null>(null);
  const [insSheets, setInsSheets] = useState<string[]>([]);
  const [insDetectedSheet, setInsDetectedSheet] = useState("");
  const [insurerSheet, setInsurerSheet] = useState("");

  // Prior-period unreconciled items (GL only). Optional: absent on every run that
  // doesn't use it, which keeps those runs identical to before the feature.
  const [unrecFile, setUnrecFile] = useState<File | null>(null);
  // The CRI-side carried file. Separate slot because a FIRST reconciliation supplies
  // the two books as two extracts; from the second month on, finance uploads the
  // previous output workbook into the slot above and this one stays empty.
  const [unrecCriFile, setUnrecCriFile] = useState<File | null>(null);
  // The previous period's OUTPUT workbook. Carries both books at once, so when it is
  // supplied it is sent as the (single) carried file and the two per-ledger pickers are
  // disabled — sending both would state the same population twice.
  const [unrecPrevFile, setUnrecPrevFile] = useState<File | null>(null);
  // The per-ledger box it was moved from when attached there (PriorPeriodPicker), else "".
  const [prevMovedFrom, setPrevMovedFrom] = useState("");
  const pickPrevFile = (f: File | null, from = "") => { setUnrecPrevFile(f); setPrevMovedFrom(from); };
  // Which sheet of each carried workbook to read. "" = let the engine scan every sheet
  // and keep the one with the most items (the behaviour before these dropdowns). Pinned
  // by the picker when the scan finds exactly one readable sheet, which is what finance's
  // two-sided workbook needs: it holds the OTHER book's list on its other tab, and the
  // most-items scan alone cannot be told which tab belongs to which ledger.
  const [unrecSheet, setUnrecSheet] = useState("");
  const [unrecCriSheet, setUnrecCriSheet] = useState("");

  // What actually goes on the wire. A previous-output workbook carries BOTH books on its
  // Group 1/2/3 tabs, so it is sent as the single carried file and the per-ledger lists
  // are suppressed — sending both would state the same population twice. Resolved in one
  // place because the prewarm, the run and the handoff must agree exactly: a divergence
  // silently changes the prematch fingerprint and wastes the warm run.
  //
  // The sheet pins travel WITH the file they belong to: a previous-output workbook is
  // resolved off its group tabs, not by sheet name, so its pins are dropped rather than
  // carried over from the per-ledger pickers they were detected on.
  // Prior-period carried-forward items are an AP feature and are HIDDEN on AR.
  //
  // AR reconciles a customer's FULL history with no period window, so there is no
  // "previous period's unreconciled list" to carry forward, and none of the carried
  // machinery has ever been exercised against real AR data. Showing it would invite an
  // upload whose figures nobody has verified. To be revisited with finance's own AR
  // files rather than guessed at now. The engine still ACCEPTS the parameters, so this
  // hides an unvalidated path rather than removing a capability.
  const showPriorPeriod = modePick === "GL" && book === "AP";

  const carriedFiles = !showPriorPeriod
    // Not offered on this book/basis, so nothing carried goes on the wire even if a
    // file is still held in state from before a book or basis switch (this component
    // is not remounted by either). Hiding the control without this would leave an
    // unvalidated carried file reaching an AR run invisibly.
    ? { insurer: null as File | null, cri: null as File | null,
        insurerSheet: "", criSheet: "" }
    : unrecPrevFile
    ? { insurer: unrecPrevFile, cri: null as File | null,
        insurerSheet: "", criSheet: "" }
    : { insurer: unrecFile, cri: unrecCriFile,
        insurerSheet: unrecSheet, criSheet: unrecCriSheet };

  // Multi-file insurer path (several workbooks or a .zip).
  const [insFiles, setInsFiles] = useState<File[]>([]);
  const [preview, setPreview] = useState<MultiPreviewEntry[]>([]);
  const [previewBusy, setPreviewBusy] = useState(false);
  const [previewErr, setPreviewErr] = useState("");
  const [mergeSel, setMergeSel] = useState<Set<string>>(new Set());

  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState("");
  const [error, setError] = useState("");
  // A failed Run renders beside the Run button, not in the top banner: the user is
  // scrolled down there when it fails, and the top banner sat ~2 screens out of view.
  const [runError, setRunError] = useState<{ lead: string; detail?: string } | null>(null);
  const runErrorRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (runError) runErrorRef.current?.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }, [runError]);
  // One-time first-run banner; localStorage so it never re-appears (zero server cost).
  const [onboardDismissed, setOnboardDismissed] = useState(
    () => localStorage.getItem("cri_recon_onboarded") === "1"
  );
  function dismissOnboard() {
    localStorage.setItem("cri_recon_onboarded", "1");
    setOnboardDismissed(true);
  }

  // Insurers for THIS book. Re-fetched when the book changes, because an insurer
  // onboarded for AP is not thereby onboarded for AR — each book has its own,
  // separately-verified layouts, so the two lists are unrelated populations.
  const [insurersLoaded, setInsurersLoaded] = useState(false);
  // Onboarded insurers hidden because they are still in testing and this account has no
  // testing access; lets an empty list say so rather than "none onboarded".
  const [hiddenTesting, setHiddenTesting] = useState(0);
  useEffect(() => {
    let stale = false;
    setInsurersLoaded(false);
    api.insurers(book).then((r) => {
      if (stale) return;
      setInsurers(r.insurers);
      setHiddenTesting(r.hidden_testing ?? 0);
      // Regular-user convenience: preselect the insurer from the last successful run
      // (most users reconcile the same insurer month after month). Never overrides a
      // pick already made, and only applies if the name is still supported.
      const last = localStorage.getItem(lastInsurerKey);
      if (last && r.insurers.some((i) => i.name === last)) {
        setPick((p) => p || last);
      }
    }).catch((e) => { if (!stale) setError(String(e)); })
      .finally(() => { if (!stale) setInsurersLoaded(true); });
    return () => { stale = true; };
  }, [book, lastInsurerKey]);

  // Live stage from the engine ("Matching transactions", 3 of 4) — see /api/progress.
  // Polled only while a run is in flight; absence just leaves the generic status text.
  const [stage, setStage] = useState<{ stage: string; index: number; total: number } | null>(null);
  // State, not a ref, because the poller must RESTART when the id changes: picking a
  // specific month launches a second run while busy is already true, so an effect keyed
  // on busy alone would keep polling the discarded speculative run's id and freeze the
  // progress bar on stale steps.
  const [jobId, setJobId] = useState("");
  useEffect(() => {
    if (!busy || !jobId) { setStage(null); return; }
    const id = jobId;
    let stop = false;
    const iv = setInterval(() => {
      api.progress(id)
        .then((p) => {
          if (stop || !p || !p.stage) return;
          setStage({ stage: p.stage, index: p.index ?? 0, total: p.total ?? 0 });
        })
        .catch(() => { /* progress is cosmetic; never surface a failure */ });
    }, 700);
    return () => { stop = true; clearInterval(iv); };
  }, [busy, jobId]);

  // Elapsed-seconds ticker while a reconcile runs — the progress bar shows life,
  // this shows how long, so a ~60s new-insurer run doesn't read as hung.
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    if (!busy) { setElapsed(0); return; }
    const t0 = Date.now();
    const iv = setInterval(() => setElapsed(Math.round((Date.now() - t0) / 1000)), 1000);
    return () => clearInterval(iv);
  }, [busy]);

  const supported = insurers.map((i) => i.name);
  // name -> distinctive aliases, so the picker can shortlist on trade names the display
  // name lacks (e.g. "union nationale" -> AL-ITTIHAD AL WATANI).
  const aliasesByName = useMemo(
    () => Object.fromEntries(insurers.map((i) => [i.name, i.aliases ?? []])),
    [insurers]
  );
  // Names flagged UNVERIFIED by /api/insurers, so the picker can tag them.
  const testingNames = useMemo(
    () => new Set(insurers.filter((i) => i.testing).map((i) => i.name)),
    [insurers]
  );
  const insurerName = pick;
  // A name the user typed that isn't in the supported list: a NEW insurer whose layout
  // must be detected (Gemini) on first run. Mirrors the old "Other (type manually)…" path.
  const isNewInsurer = !!insurerName && !supported.includes(insurerName);
  const pickedInfo = insurers.find((i) => i.name === insurerName);
  const multiFile = pickedInfo?.multi_file ?? false;

  // AR: which customer this run is for (finance 2026-11-02). Pre-selected from the CRI
  // file's preamble; the user may pick a customer reconciled before, or type a new one.
  // The run is scoped, saved and titled under their pick.
  const isArBook = book === "AR";
  const [detectedCustomer, setDetectedCustomer] = useState<ArCustomer | null>(null);
  const [knownCustomers, setKnownCustomers] = useState<ArCustomer[]>([]);
  // A customer key, NEW_CUSTOMER, or "" = follow the detected customer.
  const [customerPick, setCustomerPick] = useState("");
  const [newCustomerName, setNewCustomerName] = useState("");
  useEffect(() => {
    setDetectedCustomer(null);
    setCustomerPick("");   // a new file follows its own customer again
    if (!isArBook || !criFile) return;
    let stale = false;
    api.detectCri(criFile, undefined, book)
      .then((r) => { if (!stale && r.customer?.key) setDetectedCustomer(r.customer); })
      .catch(() => { /* advisory: the user can still pick or type the customer */ });
    return () => { stale = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [criFile, isArBook]);
  useEffect(() => {
    setKnownCustomers([]);
    if (!isArBook || !insurerName) return;
    let stale = false;
    api.arCustomers(insurerName, book)
      .then((r) => { if (!stale) setKnownCustomers(r.customers); })
      .catch(() => { /* advisory: an empty list still allows the detected or a new one */ });
    return () => { stale = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [insurerName, isArBook]);
  const customerOptions = useMemo(() => {
    const opts = [...knownCustomers];
    if (detectedCustomer && !opts.some((c) => c.key === detectedCustomer.key))
      opts.unshift({ key: detectedCustomer.key, name: detectedCustomer.name || detectedCustomer.key });
    return opts;
  }, [knownCustomers, detectedCustomer]);
  const effectiveCustomerPick = customerPick || detectedCustomer?.key || "";
  const chosenCustomer: ArCustomer | null = effectiveCustomerPick === NEW_CUSTOMER
    ? (newCustomerName.trim() ? { key: "", name: newCustomerName.trim() } : null)
    : customerOptions.find((c) => c.key === effectiveCustomerPick) ?? null;
  const customerDiffers = !!detectedCustomer && !!chosenCustomer
    && chosenCustomer.key !== detectedCustomer.key;
  // Spread into every reconcile call (prewarm, run, and the inputs re-runs reuse).
  const customerArgs = isArBook && chosenCustomer
    ? { customerKey: chosenCustomer.key, customerName: chosenCustomer.name } : {};

  // Step 1 (customer variance check) for an insurer whose layout opts in: a guided
  // wizard the user can skip. The current statement carries over to step 2.
  const rfEnabled = !!pickedInfo?.customer_rollforward;
  const [wizardStep, setWizardStep] = useState<1 | 2>(1);
  const [rfPrevFiles, setRfPrevFiles] = useState<File[]>([]);
  const [rfCurrFiles, setRfCurrFiles] = useState<File[]>([]);
  // Finance closes a month after it ends, so default to last month.
  const [rfCurrMonth, setRfCurrMonth] = useState(() => previousMonth(new Date().toISOString().slice(0, 7)));
  const [rfPrevMonth, setRfPrevMonth] = useState(() => previousMonth(rfCurrMonth));
  const [rfBusy, setRfBusy] = useState(false);
  const [rfError, setRfError] = useState("");
  const [rfResult, setRfResult] = useState<RollforwardResponse | null>(null);
  useEffect(() => { setWizardStep(1); setRfResult(null); setRfError(""); }, [insurerName]);
  const showStep1 = rfEnabled && wizardStep === 1;
  // The current period: the sidebar's window, else the current month itself.
  const rfFrom = settings.periodFrom || `${rfCurrMonth}-01`;
  const rfTo = settings.periodTo || monthEndISO(rfCurrMonth);

  // The current month sets the sidebar's From and To (both stay editable). Rows before
  // From are not dropped: the reconciliation folds them into the opening as of From.
  function monthWindow(ym: string) {
    return { periodFrom: `${ym}-01`, periodTo: monthEndISO(ym),
             periodFromCleared: false, periodToCleared: false };
  }

  function chooseCurrentMonth(ym: string) {
    setRfCurrMonth(ym);
    if (rfPrevMonth >= ym) setRfPrevMonth(previousMonth(ym));
    onSettingsChange({ ...settings, ...monthWindow(ym) });
  }

  async function runVariance() {
    setRfBusy(true);
    setRfError("");
    try {
      setRfResult(await api.customerRollforward({
        book, insurerName, previousFiles: rfPrevFiles, currentFiles: rfCurrFiles,
        previousMonth: rfPrevMonth, periodTo: rfTo, tolerance: settings.tolPremium,
        usdRate: settings.usdRate, eurRate: settings.eurRate,
      }));
    } catch (e) {
      setRfError(e instanceof Error ? e.message : String(e));
    } finally {
      setRfBusy(false);
    }
  }

  // Step 1's results follow the sidebar's Premium tolerance, as the main results page
  // does. Compared with the tolerance the SHOWN result was computed with, so coming back
  // from step 2 with a new value re-runs too; waiting on rfBusy means a value committed
  // mid-run is picked up when that run ends. rfTriedTol stops a failed re-run retrying
  // the same value in a loop (the result keeps its old tolerance on failure).
  const rfTriedTol = useRef<number | null>(null);
  useEffect(() => {
    if (!showStep1 || !rfResult || rfBusy) return;
    const tol = settings.tolPremium;
    if (tol === rfResult.summary.tolerance) { rfTriedTol.current = null; return; }
    if (tol === rfTriedTol.current) return;
    rfTriedTol.current = tol;
    void runVariance();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showStep1, rfResult, rfBusy, settings.tolPremium]);

  function continueToReconciliation() {
    if (rfCurrFiles.length) setInsFiles(rfCurrFiles);
    if (!settings.periodTo) onSettingsChange({ ...settings, ...monthWindow(rfCurrMonth) });
    setWizardStep(2);
  }

  // Per-insurer Transaction-Period defaults. Reset on every insurer change AND
  // whenever the CRI file's own span arrives, so the sidebar always shows the window
  // that will actually be applied — and never a previous insurer's. periodFrom is
  // recon-AFFECTING (it windows the engine), so a stale value must not survive the
  // switch: Dubai's 2025-01-01 leaking onto American Home silently dropped its
  // pre-2025 CRI rows.
  //
  // Precedence for From: the layout's explicit default_period_from (Dubai's
  // hardcoded 2025-01-01) ALWAYS wins; otherwise an insurer flagged
  // derive_period_from_cri takes the CRI ledger's own earliest DOC DATE — making
  // visible (and clearable) the window cri_period_floor already applies server-side.
  // That flag now tracks the FLOOR itself, so GL and OS/aging ledgers alike show the
  // window being applied; only insurers that opt out of the floor derive nothing,
  // where a date would be a control that changes nothing.
  //
  // To is NEVER seeded. A pinned upper bound is a hardcoded date that does not track
  // the ledger, so the month it was written for silently windows every LATER month's
  // transactions out of the closings — and a seeded To also makes Run skip the month
  // dialog below, removing the one surface that would have explained the cut-off.
  // The top of the range stays open unless the user sets it themselves.
  //
  // "Applied once per (insurer, span)" is load-bearing: the defaults are a STARTING
  // POINT, not a floor the UI re-imposes. Without the guard, a user who cleared
  // From to see the whole ledger would have it silently refilled the next time this
  // effect re-ran — the exact opposite of the clearable behaviour finance asked for.
  //
  // The guard lives in App (see Props.periodSeedKey), NOT in a ref here: Re-upload
  // unmounts this page, which reset a ref to "" and reopened the seed, overwriting the
  // window the user had just set. Keyed on (insurer, CRI span) so a genuine insurer
  // switch still re-seeds — a stale window must never leak across insurers (Dubai's
  // 2025-01-01 onto American Home silently dropped its pre-2025 rows).
  useEffect(() => {
    if (!pickedInfo) return;
    // Tolerances reset WITH the dates: they are per-insurer in nature (Tokio runs
    // commission 0.50, most insurers 0.00) and are recon-affecting, so one carried
    // across a switch silently changes what counts as Matched on the new insurer —
    // the same class of bug as a stale window. The FX rates deliberately do NOT reset:
    // they are a PERIOD quote finance sets per month and applies across every insurer
    // they reconcile that day, so clearing them would force a re-type per insurer.
    // A DIFFERENT insurer, not merely a new span for the same one (a re-upload must
    // leave the tolerances the user tuned alone). "" = first seed of the session, where
    // the tolerances are already at their defaults and resetting is a no-op. The name
    // is the key's suffix, so compare on the prefix rather than splitting — an insurer
    // name containing "|" would otherwise mis-compare.
    const insurerChanged = !!periodSeedKey && !periodSeedKey.startsWith(`${pickedInfo.name}|`);
    const tolReset = insurerChanged
      ? { tolerance: DEFAULT_SIDEBAR.tolerance,
          tolPremium: DEFAULT_SIDEBAR.tolPremium,
          tolCommission: DEFAULT_SIDEBAR.tolCommission }
      : {};
    // Wait for the span an insurer's From is DERIVED from. Re-upload drops criFile, so
    // the span is momentarily null: seeding here wrote From="" — not the default, and
    // not the user's value either — silently unflooring the run. Only insurers that
    // actually derive from the span need the wait; the rest (explicit default, or no
    // default at all) are unaffected by a missing span and seed immediately.
    const needsSpan = !pickedInfo.default_period_from && !!pickedInfo.derive_period_from_cri;
    if (needsSpan && !criSpan.first) {
      // The tolerance reset must NOT wait with the dates: deferred to the CRI upload, it
      // zeroed a tolerance the user had typed AFTER picking the insurer. Reset now and
      // claim the insurer (span-less key) so the span's arrival can't read as a switch.
      // The previous insurer's window goes too: left in the box it was sent with the run
      // whenever this CRI's dates could not be read (Dubai's 2025-01-01 onto Emirates).
      // Blank is this insurer's own default until the span arrives, since the server
      // applies the CRI floor itself.
      if (insurerChanged) {
        onPeriodSeeded(`${pickedInfo.name}|`);
        onSettingsChange({ ...settings, ...tolReset, periodFrom: "", periodTo: "",
                           periodFromCleared: false, periodToCleared: false });
      }
      return;
    }
    // A layout default does not depend on the span, so the CRI upload must not re-seed
    // it: that upload comes AFTER the customer-variance step, and re-seeding wiped the
    // month window step 1 had set (Dubai: From back to 2025-01-01, To cleared).
    const key = `${pickedInfo.name}|${pickedInfo.default_period_from ? "" : (criSpan.first ?? "")}`;
    if (periodSeedKey === key) return;  // already seeded; respect edits
    onPeriodSeeded(key);
    const from = pickedInfo.default_period_from
      ?? (pickedInfo.derive_period_from_cri ? criSpan.first : null)
      ?? "";
    const to = "";
    if (from !== settings.periodFrom || to !== settings.periodTo
        || Object.keys(tolReset).length) {
      // Seeding a fresh default is not a clear — drop any latched clear from the
      // previous insurer, or the new insurer would silently run unfloored.
      onSettingsChange({ ...settings, ...tolReset, periodFrom: from, periodTo: to,
                         periodFromCleared: false, periodToCleared: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pickedInfo?.name, criSpan.first, periodSeedKey]);

  // What the seed effect above WOULD put in the From box for this insurer, and where it
  // comes from. The Run dialog compares the From actually in force against this to say
  // who set the window: the box is pre-filled for every insurer the CRI floor applies
  // to, so a bare date cannot distinguish a system-applied floor from a date the user
  // chose — and the dialog was calling both "your Transaction Period From".
  //
  // DERIVED, not remembered from the effect: the effect short-circuits on an unchanged
  // periodSeedKey (`already seeded; respect edits`), and a re-upload REMOUNTS this page,
  // so a stored copy would be empty on exactly the path where the seed is still in the
  // box — misattributing the floor to the user. Same precedence as the effect; keep the
  // two in lockstep.
  const seededFrom: { value: string; source: "cri" | "layout" | "none" } =
    pickedInfo?.default_period_from
      ? { value: pickedInfo.default_period_from, source: "layout" }
      : (pickedInfo?.derive_period_from_cri && criSpan.first)
        ? { value: criSpan.first, source: "cri" }
        : { value: "", source: "none" };

  // Per-insurer OS/GL basis. Every onboarded layout declares cri_basis, and the engine
  // already honours it (backend._resolve_cri_mode step 2) — but the UI always sent an
  // explicit mode_override, so that step never fired over HTTP and every run was pinned
  // to "OS". Seeding the radio here makes the declared basis the visible default for the
  // six GL insurers (AL-ITTIHAD, AL BUHAIRA, AL WATHBA, both Dubais, NIA) without the
  // finance user having to remember which is which. Insurers declaring nothing keep "OS".
  // Skipped once the user has touched the radio: an explicit override outranks the layout.
  useEffect(() => {
    if (modeTouched || !pickedInfo) return;
    setModePick(pickedInfo.cri_basis ?? "OS");
  }, [pickedInfo?.name, pickedInfo?.cri_basis, modeTouched]);

  async function onCriChosen(files: File[]) {
    setCriFiles(files);
    const file = files[0];
    if (!file) return;
    setCriErr("");
    setError("");
    setRunError(null);
    try {
      const [detect, sheets] = await Promise.all([api.detectCri(file), api.listSheets(file)]);
      setMode(detect.mode);
      // Deliberately does NOT set modePick: detect.mode is always "OS" (the CRI detector
      // locates the sheet, it does not sense a basis), so assigning it here would reset a
      // GL insurer's seeded basis every time a CRI file was chosen.
      setCriSheet(detect.sheet);
      setCriDetectedSheet(detect.sheet);
      setCriSheets(sheets.sheets);
      setCriSpan({ first: detect.first_date, last: detect.last_date });
    } catch (e) {
      setCriErr("Could not read CRI file: " + String(e));
      setCriSpan({ first: null, last: null });
    }
  }

  // The period prefill comes from the CRI ledger's span, and the span is a property of
  // the SHEET being parsed — so overriding the sheet must re-read it. Without this the
  // From box kept the detected sheet's dates (or stayed blank, when that sheet had no
  // readable DOC DATE) while the engine floored on the sheet actually parsed: an empty
  // box over a real, invisible window. Skipped while the pick still equals the detected
  // sheet, whose span onCriChosen already fetched.
  useEffect(() => {
    if (!criFile || !criSheet || criSheet === criDetectedSheet) return;
    let stale = false;
    api.detectCri(criFile, criSheet)
      .then((r) => { if (!stale) setCriSpan({ first: r.first_date, last: r.last_date }); })
      .catch(() => { /* advisory: leave the last known span rather than blanking it */ });
    return () => { stale = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [criFile, criSheet, criDetectedSheet]);

  // Single path: list the workbook's sheets whenever the file changes, and re-run the
  // cached-layout sheet detection whenever the FILE or the INSURER changes (choosing
  // the insurer after the file must still auto-detect — the old code missed that).
  useEffect(() => {
    if (!insurerFile) { setInsSheets([]); setInsDetectedSheet(""); setInsurerSheet(""); return; }
    let stale = false;
    api.listSheets(insurerFile)
      .then((r) => { if (!stale) setInsSheets(r.sheets); })
      .catch((e) => {
        if (stale) return;
        setInsSheets([]);
        toast.error(`Could not list sheets in ${insurerFile.name}: ${String(e)}`);
      });
    return () => { stale = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [insurerFile]);

  // The insurer span, for BOTH upload paths. Single-file follows the SHEET actually
  // selected (the user can override the detected one); multi-file spans every uploaded
  // workbook, which is what the merge ingests. Covering multi-file matters: half the
  // roster is multi-file, and skipping it silently reinstated the CRI-only month bound
  // (AL WATHBA's CRI starts 2026-06-02 but its statement starts 2026-04-01, so April
  // and May vanished from the dialog). Advisory: a failure leaves the span null and the
  // dialog falls back to CRI-only behaviour.
  const spanFiles = multiFile ? insFiles : (insurerFile ? [insurerFile] : []);
  const spanKey = spanFiles.map((f) => `${f.name}:${f.size}`).join("|");
  useEffect(() => {
    if (!insurerName || spanFiles.length === 0 || (!multiFile && !insurerSheet)) {
      setInsSpan({ first: null, last: null });
      return;
    }
    let stale = false;
    api.insurerSpan(insurerName, spanFiles, multiFile ? "" : insurerSheet, book)
      .then((r) => { if (!stale) setInsSpan({ first: r.first_date, last: r.last_date }); })
      .catch(() => { if (!stale) setInsSpan({ first: null, last: null }); });
    return () => { stale = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    // `book` is a dep: it selects WHICH layout resolves the span, so a book switch
    // must re-read it rather than keep the other book's answer.
  }, [spanKey, insurerName, multiFile, insurerSheet, book]);

  useEffect(() => {
    if (!insurerFile || !insurerName || multiFile) return;
    let stale = false;
    api.detectSoa(insurerName, insurerFile, book)
      .then((r) => {
        if (stale) return;
        setInsDetectedSheet(r.sheet || "");
        if (r.sheet) setInsurerSheet(r.sheet);
      })
      .catch(() => {
        if (stale) return;
        setInsDetectedSheet("");
        toast.info("Could not auto-detect the statement sheet — pick one manually below.");
      });
    return () => { stale = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    // ...and likewise for the detected SOA sheet.
  }, [insurerFile, insurerName, multiFile, book]);

  // Multi path: preview which sheets the merge will ingest; default-select every
  // matched sheet the server did not pre-untick (month tabs superseded by a newer month
  // of the same account, and sheets whose documents all sit on another sheet — see
  // backend _mark_latest_period_sheets / _mark_subset_sheets).
  useEffect(() => {
    if (!multiFile || insFiles.length === 0 || !insurerName) { setPreview([]); setMergeSel(new Set()); setPreviewErr(""); return; }
    let stale = false;
    setPreviewBusy(true);
    setPreviewErr("");
    api.previewMulti(insurerName, insFiles)
      .then((r) => {
        if (stale) return;
        setPreview(r.preview);
        setMergeSel(new Set(r.preview
          .filter((m) => m.matched && m.default_selected !== false)
          .map((m) => mergeKey(m.fname, m.sheet))));
      })
      .catch((e) => {
        if (stale) return;
        // Preview failed (e.g. an over-cap upload rejected server-side): drop any stale
        // matched-sheet state and surface the reason IN the caption, so the file line
        // never claims "Loaded …" for a file the server refused.
        setPreview([]); setMergeSel(new Set());
        setPreviewErr(e instanceof Error ? e.message : String(e));
      })
      .finally(() => { if (!stale) setPreviewBusy(false); });
    return () => { stale = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [multiFile, insFiles, insurerName]);

  // Switching between a single-file and a multi-file insurer drops the other path's
  // stale state (mirrors the session-state clears in app/upload.py).
  useEffect(() => {
    if (multiFile) { setInsurerFile(null); setInsurerSheet(""); setInsDetectedSheet(""); }
    else { setInsFiles([]); setPreview([]); setMergeSel(new Set()); }
  }, [multiFile]);

  const matched = useMemo(() => preview.filter((m) => m.matched), [preview]);
  const ignored = useMemo(() => preview.filter((m) => !m.matched && !m.unreadable), [preview]);
  // Files no reader could open: named on their own line, never as ignored sheets.
  const unreadable = useMemo(() => preview.filter((m) => m.unreadable), [preview]);
  // One note per file whose converted statement does not foot (the file is still merged).
  const fileWarnings = useMemo(
    () => [...new Map(preview.filter((m) => m.warnings?.length)
      .map((m) => [m.fname, m.warnings ?? []] as const)).entries()],
    [preview]);
  // Matched sheets the server pre-unticked (superseded month tabs). Read off the
  // PREVIEW, not mergeSel, so the caption keeps explaining the default after the
  // user starts ticking boxes.
  const preUnticked = useMemo(
    () => matched.filter((m) => m.default_selected === false && !m.superseded_by).length, [matched]);
  // Sheets pre-unticked because another sheet already holds all their documents.
  const supersededSheets = useMemo(
    () => matched.filter((m) => m.default_selected === false && m.superseded_by), [matched]);
  const workbookNames = useMemo(
    () => [...new Set(preview.filter((m) => !m.unreadable).map((m) => m.fname))], [preview]);
  // An empty preview falls back to the uploaded names, but "Loaded" must never name a
  // file the server could not read.
  const loadedNames = workbookNames.length ? workbookNames
    : (unreadable.length ? [] : insFiles.map((f) => f.name));

  const criOk = !!criFile && !criErr;
  const multiMode = multiFile && insFiles.length > 0;
  // A new (uncached) insurer needs Gemini detection; block the run for users who lack
  // that permission (the backend enforces the same 403, this is the friendly gate).
  const geminiBlocked = isNewInsurer && !canUseGemini;
  // Form readiness, independent of whether a run is in flight. The month dialog is
  // confirmed WHILE the speculative run is going (busy=true), so its handler must gate
  // on this, not on canRun — gating on canRun there deadlocks the dialog.
  const formReady = criOk && !!insurerName && !geminiBlocked && (!isArBook || !!chosenCustomer) && (
    multiMode ? mergeSel.size > 0 : (!!insurerFile && !!insurerSheet)
  );
  const canRun = formReady && !busy;

  const waitingFor: string[] = [];
  if (!criFile) waitingFor.push("CRI file");
  else if (criErr) waitingFor.push("CRI file (parse error — try picking a specific sheet)");
  if (!insurerName) waitingFor.push("Insurer Name");
  else if (geminiBlocked) waitingFor.push("a supported insurer (Gemini detection not permitted for your account)");
  else if (isArBook && !chosenCustomer) waitingFor.push("the customer");
  if (multiFile) {
    if (insFiles.length === 0) waitingFor.push("Insurer SOA file(s)");
    else if (mergeSel.size === 0 && !previewBusy) waitingFor.push("at least one sheet to merge");
  } else {
    if (!insurerFile) waitingFor.push("Insurer SOA file");
    else if (!insurerSheet) waitingFor.push("Insurer Sheet selection");
  }

  // Step state for the two upload cards, derived from the SAME predicates as waitingFor
  // above — no second source of truth about what a complete form is. The current step
  // is the first one not yet done, so at most one card is ever highlighted.
  const criStepDone = !!criFile && !criErr;
  const insurerStepDone = !!insurerName && !geminiBlocked && (!isArBook || !!chosenCustomer) && (multiFile
    ? insFiles.length > 0 && (mergeSel.size > 0 || previewBusy)
    : !!insurerFile && !!insurerSheet);
  const activeStep = !criStepDone ? 0 : !insurerStepDone ? 1 : 2;
  const stepState = (i: number, done: boolean) =>
    done ? "done" : i === activeStep ? "active" : "todo";

  // The upload boxes accept drops, so a file released just outside one must not fall
  // through to the browser, which opens it in place of the app and loses the form.
  useEffect(() => {
    const block = (e: DragEvent) => { if (e.dataTransfer?.types.includes("Files")) e.preventDefault(); };
    window.addEventListener("dragover", block);
    window.addEventListener("drop", block);
    return () => { window.removeEventListener("dragover", block); window.removeEventListener("drop", block); };
  }, []);

  function onInsurerFile(file: File) {
    setInsurerFile(file);
    setError("");
    setRunError(null);
  }

  // Build the exact ReconSettings a Run would send. Shared by run() and the prewarm
  // below so the two can NEVER key different fingerprints (that would make prewarm a
  // wasted call instead of a cache hit).
  function buildSettings(): ReconSettings {
    return {
      tolerance: settings.tolerance,
      amount_match_tol: settings.tolPremium,
      amount_tol_premium: settings.tolPremium,
      amount_tol_commission: settings.tolCommission,
      usd_rate: settings.usdRate > 0 ? settings.usdRate : DEFAULT_USD_RATE,
      // EUR must be sent for the SAME reason as USD: the rates are applied to the
      // schema at PARSE time, so they change every downstream figure. Omitting it
      // let the FIRST run of a session silently reconcile at the server default
      // while the sidebar showed the user's saved rate — and ResultPage seeds
      // lastKey from the current sidebarKey, so no re-run ever corrected it.
      eur_rate: settings.eurRate > 0 ? settings.eurRate : DEFAULT_EUR_RATE,
      mode_override: modePick,
      cri_sheet_override: criSheet,
      period_from: settings.periodFrom,
      period_from_cleared: settings.periodFromCleared,
      period_to: settings.periodTo,
    };
  }

  function buildSheetFilter(): [string, string][] | undefined {
    const allKeys = new Set(matched.map((m) => mergeKey(m.fname, m.sheet)));
    const narrowed = multiMode && mergeSel.size > 0 && mergeSel.size < allKeys.size;
    return narrowed
      ? matched.filter((m) => mergeSel.has(mergeKey(m.fname, m.sheet))).map((m) => [m.fname, m.sheet])
      : undefined;
  }

  // SPECULATIVE PREWARM — the single biggest latency win available.
  //
  // Every input to the prematch fingerprint (file bytes + insurer + sheets + basis +
  // tolerances + window + USD rate) is known BEFORE the user presses Run. The prematch
  // is content-addressed and idempotent, so computing it now yields the byte-identical
  // blob the real Run would compute — pressing Run then becomes a cache hit (~10s ->
  // ~0.4s). Cost to the server is zero net: it is the same work, moved earlier.
  //
  // Guards, all load-bearing:
  //  - `canRun` only: never fire on a half-filled form.
  //  - NEVER for a new insurer (isNewInsurer) — that path calls Gemini, and a
  //    speculative paid API call the user never asked for is unacceptable.
  //  - 1.2s debounce + AbortController: any input change cancels the in-flight prewarm,
  //    so a user still choosing sheets doesn't queue up runs.
  //  - Fire once per distinct input set (prewarmedKey), so re-renders can't re-post.
  //  - Silent on every failure: a prewarm is an optimisation. Any real error will
  //    surface properly on the actual Run.
  const prewarmedKey = useRef<string>("");
  useEffect(() => {
    if (!canRun || isNewInsurer || busy) return;
    const files = multiMode ? insFiles : (insurerFile ? [insurerFile] : []);
    if (!criFile || files.length === 0) return;
    // Identity of this exact upload+settings combination.
    const key = [
      criFiles.map((f) => `${f.name}:${f.size}:${f.lastModified}`).join("|"),
      // The book selects which layout the run resolves, so it keys the prematch
      // server-side and must key the prewarm too: without it a warm run from the other
      // book would be reused under this one.
      book,
      criSheet, modePick, insurerName,
      multiMode ? "" : insurerSheet,
      files.map((f) => `${f.name}:${f.size}:${f.lastModified}`).join("|"),
      JSON.stringify(buildSheetFilter() ?? null),
      settings.tolPremium, settings.tolCommission, settings.usdRate,
      settings.periodFrom ?? "", settings.periodTo ?? "",
      // The unreconciled file keys the prematch server-side, so it must key the
      // prewarm too - otherwise the warm run and the real Run use different
      // fingerprints and the prewarm is silently wasted.
      carriedFiles.insurer
        ? `${carriedFiles.insurer.name}:${carriedFiles.insurer.size}:${carriedFiles.insurer.lastModified}`
        : "",
      carriedFiles.cri
        ? `${carriedFiles.cri.name}:${carriedFiles.cri.size}:${carriedFiles.cri.lastModified}`
        : "",
      // The sheet pins key the prematch server-side too (api/service.py), so a pin
      // changed after the prewarm must invalidate it like any other input.
      carriedFiles.insurerSheet, carriedFiles.criSheet,
      // The chosen AR customer keys the prematch server-side too.
      customerArgs.customerKey ?? "", customerArgs.customerName ?? "",
    ].join("~");
    if (prewarmedKey.current === key) return;

    const ac = new AbortController();
    const t = setTimeout(() => {
      prewarmedKey.current = key;
      api.reconcile({
        book,
        insurerName,
        insurerSheetName: multiMode ? "" : insurerSheet,
        criFiles,
        insurerFiles: files,
        settings: buildSettings(),
        insurerSheetFilter: buildSheetFilter(),
        unreconciledFile: carriedFiles.insurer,
        unreconciledSheetName: carriedFiles.insurerSheet,
        unreconciledCriFile: carriedFiles.cri,
        unreconciledCriSheetName: carriedFiles.criSheet,
        ...customerArgs,
        prewarm: true,
        signal: ac.signal,
      }).catch(() => {
        // Aborted or failed: forget the key so a later attempt (or the real Run) is
        // free to try again. Never surfaced to the user.
        if (prewarmedKey.current === key) prewarmedKey.current = "";
      });
    }, 1200);
    return () => { clearTimeout(t); ac.abort(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canRun, isNewInsurer, busy, book, criFiles, criSheet, modePick, insurerName,
      insurerSheet, insFiles, insurerFile, multiMode, mergeSel, settings.tolPremium,
      settings.tolCommission, settings.usdRate, settings.periodFrom, settings.periodTo,
      unrecFile, customerArgs.customerKey, customerArgs.customerName]);

  // Month dialog: opened by Run, answered WHILE the reconciliation is already in
  // flight. Automatic (the default) needs no re-run at all; a specific month only
  // re-runs when its cutoff differs from what the speculative run already used.
  const [monthOpen, setMonthOpen] = useState(false);
  const pendingRun = useRef<Promise<ReconcileResponse> | null>(null);

  function startRun() {
    if (!criFile || !canRun) return;
    setError("");
    setRunError(null);
    // SKIP THE DIALOG when the user already set a Transaction Period "To". The dialog's
    // only job is to set the as-of date, and an explicit period_to ALREADY does that:
    // the engine sets as_of_date = period_to (backend/reconcile.py, the "_eff_period_to"
    // block), so answering it cannot change a single figure. Measured on Emirates with
    // From+To set: as-of, both closings, the difference and every row count are
    // identical with and without a month override. Asking anyway made the user confirm
    // a cutoff they had just typed — and only at month-end granularity, which cannot
    // even express a mid-month To.
    //
    // A "From" alone deliberately does NOT skip: From bounds the BOTTOM of the window
    // and leaves the as-of auto-derived, so the question is still a real one.
    if (settings.periodTo) {
      setMonthOpen(false);
      pendingRun.current = null;
      void run("");   // Automatic: period_to already carries the cutoff
      return;
    }
    setMonthOpen(true);
    // Fire the reconciliation NOW, under Automatic. The user answers over the top of
    // it, so the common case (Automatic, or a month at/after the ledger end) has the
    // result ready the moment they confirm.
    const p = launch("");
    // The speculative run is DISCARDED when the user picks a specific month, so it
    // must never reject unhandled. The real failure still surfaces: run() awaits this
    // same promise on the Automatic path, where the rejection is caught properly.
    p.catch(() => {});
    pendingRun.current = p;
  }

  async function run(reconMonth: string) {
    // NOT canRun: this fires while the speculative run is still in flight (busy).
    if (!criFile || !formReady) return;
    // Reuse the in-flight speculative run ONLY for Automatic, where it is the very
    // same request. A specific month always re-runs: it sets the reported as-of, and
    // criSpan is the CRI ledger's span alone (cri_date_span is advisory and CRI-only)
    // while the engine's as-of spans BOTH books — so it cannot prove a month is a
    // no-op. Re-running is cheap here: the prematch is content-addressed, so a month
    // that changes nothing upstream still hits the cache for the expensive stage.
    const inflight = pendingRun.current;
    pendingRun.current = null;
    // Close BEFORE the await, not in the finally: the specific-month path re-runs, and
    // a modal held up across that wait reads as a frozen dialog. Dismissing it hands
    // the user back the page's own stage label and progress bar, which is where the
    // wait is legible.
    setMonthOpen(false);
    try {
      const resp = await (reconMonth === "" && inflight ? inflight : launch(reconMonth));
      localStorage.setItem(lastInsurerKey, insurerName);
      const reconSettings: ReconSettings = { ...buildSettings(), recon_month: reconMonth };
      onReconciled(resp, {
        criFiles,
        insurerFiles: multiMode ? insFiles : (insurerFile ? [insurerFile] : []),
        insurerName,
        insurerSheetName: multiMode ? "" : insurerSheet,
        book,
        settings: reconSettings,
        insurerSheetFilter: buildSheetFilter(),
        // Must ride along: every re-run re-sends it, or the prior-period block is
        // rebuilt from nothing and vanishes (see ReconInputs). The sheet pins go with
        // it for the same reason — a re-run without them falls back to the most-items
        // scan and can read the OTHER book's tab off a two-sided workbook.
        unreconciledFile: carriedFiles.insurer,
        unreconciledSheetName: carriedFiles.insurerSheet,
        unreconciledCriFile: carriedFiles.cri,
        unreconciledCriSheetName: carriedFiles.criSheet,
        ...customerArgs,
      });
    } catch (e) {
      showRunError(e);
    } finally {
      setBusy(false);
      setStatus("");
    }
  }

  // One reconcile call at the given month ("" = Automatic / no override).
  async function launch(reconMonth: string): Promise<ReconcileResponse> {
    // New id per run so a poll can never report a previous run's stage.
    const id = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
    setJobId(id);
    setStage(null);
    setBusy(true);
    // Fallback text only — once the engine publishes its first stage the live label
    // below replaces this. A supported insurer resolves from the on-disk layout cache
    // with zero Gemini calls; an "Other" (new) insurer detects its layout first.
    setStatus(isNewInsurer
      ? "Reading files · resolving insurer layout (detecting — first run can take a minute) · matching · building summary…"
      : "Reading files · resolving layout (cached) · matching · building summary…");
    // Same builders the prewarm uses, so both key the identical prematch fingerprint
    // (a divergence would silently turn the prewarm into a wasted call). The
    // sheets-to-merge narrowing is sent only when the user deselected a matched sheet —
    // an untouched upload keeps the engine's default path, exactly like Streamlit.
    return api.reconcile({
      jobId: id,
      book,
      insurerName,
      insurerSheetName: multiMode ? "" : insurerSheet,
      criFiles,
      insurerFiles: multiMode ? insFiles : (insurerFile ? [insurerFile] : []),
      settings: { ...buildSettings(), recon_month: reconMonth },
      insurerSheetFilter: buildSheetFilter(),
      unreconciledFile: carriedFiles.insurer,
      unreconciledSheetName: carriedFiles.insurerSheet,
      unreconciledCriFile: carriedFiles.cri,
      unreconciledCriSheetName: carriedFiles.criSheet,
      ...customerArgs,
      // FIGURES FIRST. The dashboard this response opens paints without reading a single
      // transaction row, and the rows are ~98.6% of the payload — ResultPage fetches them
      // straight afterwards and gates the statement view until they land.
      includeFrames: false,
    });
  }

  // Map the failure to something a first-time user can act on. 403 (permission) and
  // 422 (parse/layout — the server sends a human-readable cause) surface the server
  // detail; anything else gets a next-step hint instead of a bare 500.
  function showRunError(e: unknown) {
    const detail = e instanceof Error ? e.message : String(e);
    if (e instanceof ApiError && e.status === 403) {
      setRunError({ lead: detail });
    } else if (e instanceof ApiError && e.status === 422 && detail.startsWith("layout detection failed")) {
      // Prefix set by api/routes_reconcile.py's LayoutDetectionError handler. The raw text stays
      // underneath: it names the sheet and the Gemini cause support needs.
      setRunError({
        lead: `We couldn't recognise the layout of ${insurerName}'s statement. Check that ` +
          "the right insurer and sheet are picked, then re-run. If it keeps failing, send " +
          "the details below to your administrator.",
        detail,
      });
    } else if (e instanceof ApiError && e.status === 422) {
      setRunError({ lead: `Couldn't process the files: ${detail} — check that the CRI sheet and ` +
        "Insurer sheet are the right ones and the OS/GL basis matches the file, then re-run." });
    } else {
      setRunError({ lead: `Reconciliation failed unexpectedly (${detail}). Re-select the sheets, ` +
        "confirm the basis, and try again. If it persists, contact support with the " +
        "insurer name and the files you uploaded." });
    }
    toast.error("Reconciliation did not run — see the details under the Run button.");
  }

  return (
    <div className="app-main">
      <ReconMonthDialog
        open={monthOpen}
        criSpan={criSpan}
        insurerSpan={insSpan}
        periodFrom={settings.periodFrom}
        seededFrom={seededFrom.value}
        seededFromSource={seededFrom.source}
        derivePeriodFromCri={pickedInfo?.derive_period_from_cri}
        running={busy}
        stageText={stage ? `${stage.stage}…` : ""}
        onConfirm={run}
        onCancel={() => { pendingRun.current = null; setMonthOpen(false); setBusy(false); setStatus(""); }}
      />
      <div className="sidebar-layout">
        <Sidebar settings={settings} onChange={onSettingsChange} collapsed={sidebarCollapsed} onToggle={onToggleSidebar}
          savedRates={savedRates} rateNoticeAck={rateNoticeAck} onAckRateNotice={onAckRateNotice}
          book={book} />
        {showStep1 && rfResult ? (
          <RollforwardPage result={rfResult} insurerName={insurerName} book={book}
            rerunning={rfBusy} rerunError={rfError}
            onBack={() => setRfResult(null)} onContinue={continueToReconciliation} />
        ) : (
        <div className="page-body">
          <h1 className="page-title">CRI x Insurer - Reconciliation</h1>
          {!onboardDismissed && (
            <div className="onboard-banner">
              <span>
                <strong>New here?</strong> Upload two files — your own CRI ledger on the left and
                the Statement of Account (SOA) the insurer sent you on the right — then press
                Run Reconciliation. You'll get a side-by-side difference with drill-down groups,
                manual matching, notes, and an annotated Excel export. Hover any{" "}
                <InfoTip text="Tips like this one explain every term as you go." /> for a definition.
              </span>
              <button className="btn" style={{ flexShrink: 0 }} onClick={dismissOnboard}>Got it</button>
            </div>
          )}
          {!showStep1 && (
            <>
              <p className="muted" style={{ marginTop: -6 }}>
                Upload both files below. The app pre-selects the basis for the insurer you pick, and auto-detects the sheet, balances, and period.
                Press Run Reconciliation to proceed.
              </p>
              <hr className="rule" />
            </>
          )}
          {error && (
            <div className="badge badge-risk" style={{ display: "block", marginBottom: 16 }}>{error}</div>
          )}
          {rfEnabled && (
            <ol className="rf-stepper" aria-label="Steps">
              {([[1, "Customer variance check", rfResult ? "Checked" : "Recommended"],
                 [2, "Reconciliation", "CRI ledger vs statement"]] as const).map(([n, label, sub]) => {
                const done = n === 1 && !!rfResult && wizardStep !== 1;
                return (
                  <li key={n}>
                    <button type="button" aria-current={wizardStep === n ? "step" : undefined}
                      className={`rf-stepper-step${wizardStep === n ? " active" : ""}${done ? " done" : ""}`}
                      onClick={() => setWizardStep(n)}>
                      <span className="rf-stepper-n" aria-hidden="true">{done ? "✓" : n}</span>
                      <span className="rf-stepper-text">
                        <span className="rf-stepper-label">Step {n} · {label}</span>
                        <span className="rf-stepper-sub">{sub}</span>
                      </span>
                    </button>
                  </li>
                );
              })}
            </ol>
          )}
          {showStep1 && (
            <CustomerVarianceStep
              insurerName={insurerName}
              insurerPicker={<InsurerCombobox options={supported} value={pick} onChange={setPick}
                aliasesByName={aliasesByName} testingNames={testingNames} />}
              previousMonth={rfPrevMonth}
              currentMonth={rfCurrMonth}
              onPreviousMonth={setRfPrevMonth}
              onCurrentMonth={chooseCurrentMonth}
              periodFrom={rfFrom}
              periodTo={rfTo}
              previousFiles={rfPrevFiles}
              currentFiles={rfCurrFiles}
              onPreviousFiles={setRfPrevFiles}
              onCurrentFiles={setRfCurrFiles}
              busy={rfBusy}
              error={rfError}
              onRun={runVariance}
              onSkip={continueToReconciliation}
            />
          )}
          <div style={showStep1 ? { display: "none" } : undefined}>
          <div className="row">
            <div className={`col card upload-card upload-card--${stepState(0, criStepDone)}`}>
              <h3 className="card-title">
                <span className="card-step" aria-hidden="true">{criStepDone ? "✓" : 1}</span>
                CRI Source File
                <InfoTip text="CRI = your internal ledger (the broker's books). This is YOUR export, not the insurer's file." />
              </h3>
              <p className="muted" style={{ margin: "0 0 12px" }}>Outstanding Statement (OS) or General Ledger (GL)</p>
              <FilePicker accept=".xlsx,.xls,.xlsb" multiple required
                status={criErr ? "error" : criFile ? "done" : undefined}
                label={criFile ? "Replace CRI file(s)…" : "Choose or drop CRI file(s) (.xlsx/.xls/.xlsb)"}
                onFiles={(f) => onCriChosen(f)} />
              {criFiles.map((f) => (
                <FilePreviewCard key={f.name} filename={f.name} loaded={!criErr}
                  error={criErr || undefined} sheet={criSheet} mode={modePick} />
              ))}
              {criFile && !criErr && (
                <>
                  <label className="field-label" htmlFor="cri-sheet">Sheet to parse</label>
                  <select id="cri-sheet" className="select" value={criSheet} onChange={(e) => setCriSheet(e.target.value)}>
                    {criSheets.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                  {criDetectedSheet && criSheet === criDetectedSheet && (
                    <p className="muted note">✓ Auto-detected sheet <strong>{criDetectedSheet}</strong>.</p>
                  )}
                </>
              )}
              {/* The CRI side's own prior-period open items, beside the CRI ledger they
                  belong to. GL only. Not needed once a previous output file is supplied
                  below - that carries both books. */}
              {showPriorPeriod && (
                <PriorPeriodPicker
                  side="CRI"
                  file={unrecCriFile}
                  onPick={setUnrecCriFile}
                  onPreviousOutput={(f) => pickPrevFile(f, "CRI")}
                  disabled={!!unrecPrevFile}
                  insurerName={insurerName}
                  sheet={unrecCriSheet}
                  onSheet={setUnrecCriSheet}
                />
              )}
            </div>

            <div className={`col card upload-card upload-card--${stepState(1, insurerStepDone)}`}>
              <h3 className="card-title">
                <span className="card-step" aria-hidden="true">{insurerStepDone ? "✓" : 2}</span>
                Insurer SOA
                <InfoTip text="SOA = Statement of Account: the ledger the insurance company sends you. The reconciliation compares it against your CRI ledger." />
              </h3>
              <p className="muted" style={{ margin: "0 0 12px" }}>Statement of Account from the insurance company</p>

              <label className="field-label" style={{ marginTop: 0 }}>Step 1 — Insurer</label>
              {insurersLoaded && insurers.length === 0 ? (
                // A book with no onboarded insurers, said plainly. Each book has its own
                // layouts, so this is the normal state of a book nobody has onboarded an
                // insurer into yet — not a failure, and not something the user should
                // have to infer from an empty dropdown.
                hiddenTesting > 0 ? (
                  <p className="badge badge-risk" style={{ display: "block", margin: "6px 0 0" }}>
                    No verified insurers are available for <strong>{BOOK_LABELS[book]}</strong> on
                    your account yet. {hiddenTesting === 1 ? "1 insurer is" : `${hiddenTesting} insurers are`} onboarded
                    but still in testing; ask an administrator for testing access to reconcile{" "}
                    {hiddenTesting === 1 ? "it" : "them"}.
                  </p>
                ) : (
                <p className="badge badge-risk" style={{ display: "block", margin: "6px 0 0" }}>
                  No insurers are onboarded for <strong>{BOOK_LABELS[book]}</strong> yet, so
                  there is nothing to reconcile on this book. Each book has its own layouts —
                  an insurer set up for the other book is not automatically available here.
                </p>
                )
              ) : (
                <InsurerCombobox options={supported} value={pick} onChange={setPick}
                  aliasesByName={aliasesByName} testingNames={testingNames} />
              )}
              {isArBook && insurerName && (
                <>
                  <label className="field-label" htmlFor="ar-customer">
                    Customer
                    <InfoTip text="Picked from the CRI file when it names the customer. Change it to another customer already reconciled for this insurer, or add a new one. The reconciliation is saved and titled under the customer chosen here." />
                  </label>
                  <select id="ar-customer" className="select" value={effectiveCustomerPick}
                    onChange={(e) => setCustomerPick(e.target.value)}>
                    {!effectiveCustomerPick && <option value="">Choose a customer…</option>}
                    {customerOptions.map((c) => (
                      <option key={c.key} value={c.key}>{c.name} ({c.key})</option>
                    ))}
                    <option value={NEW_CUSTOMER}>New customer…</option>
                  </select>
                  {effectiveCustomerPick === NEW_CUSTOMER && (
                    <input className="input" style={{ marginTop: 6 }} aria-label="New customer name"
                      placeholder="Customer name, as the reconciliation should be titled"
                      value={newCustomerName} onChange={(e) => setNewCustomerName(e.target.value)} />
                  )}
                  {detectedCustomer && chosenCustomer?.key === detectedCustomer.key && (
                    <p className="muted note">✓ Auto-detected from the CRI file.</p>
                  )}
                  {customerDiffers && chosenCustomer && detectedCustomer && (
                    <p className="badge badge-risk" style={{ display: "block", margin: "6px 0 0" }}>
                      The CRI file names <strong>{detectedCustomer.name}</strong> ({detectedCustomer.key}),
                      not this customer. The run will be saved and titled as{" "}
                      <strong>{chosenCustomer.name}</strong>.
                    </p>
                  )}
                </>
              )}
              {isNewInsurer && canUseGemini && (
                <p className="muted note">
                  <strong>{insurerName}</strong> is a new insurer. Its name is used as the layout
                  cache key — the layout is detected on first run (needs a Gemini key); later runs
                  are instant from the cache.
                </p>
              )}
              {isNewInsurer && !canUseGemini && (
                <p className="badge badge-risk" style={{ display: "block", margin: "6px 0 0" }}>
                  <strong>{insurerName}</strong> is a new insurer that needs layout detection
                  (Gemini), which your account isn't permitted to use. Pick a supported insurer,
                  or ask an admin to enable Gemini detection for you.
                </p>
              )}

              <label className="field-label">Step 2 — Upload statement{multiFile ? "(s)" : ""}</label>
              {!insurerName ? (
                <p className="muted" style={{ margin: "4px 0 0" }}>Select an insurer above to choose your file(s).</p>
              ) : multiFile ? (
                <>
                  <p className="muted" style={{ margin: "0 0 8px" }}>
                    This insurer's statement can arrive as one file or several. Upload one or more
                    files (or a single .zip) — the system finds every statement sheet it recognises
                    and merges them; unrelated sheets are ignored.
                  </p>
                  <FilePicker accept=".xlsx,.xls,.xlsb,.pdf,.csv,.zip" multiple required
                    status={previewErr ? "error" : insFiles.length ? "done" : undefined}
                    label={insFiles.length ? "Add more insurer files…" : "Choose or drop insurer files (one or many, a .pdf, or a .zip)"}
                    onFiles={(f) => setInsFiles((prev) => [...prev, ...f])} />
                  {insFiles.length > 0 && (
                    <button className="btn" style={{ marginTop: 8 }} onClick={() => setInsFiles([])}>
                      Remove all files
                    </button>
                  )}
                  {insFiles.length > 0 && (
                    previewBusy ? (
                      <p className="muted note-lg">Scanning uploaded files…</p>
                    ) : previewErr ? (
                      <p className="badge badge-risk" style={{ display: "block", margin: "8px 0 0" }}>
                        {previewErr}
                      </p>
                    ) : (
                      <>
                        {loadedNames.length > 0 && (
                          <p className="muted note-lg">
                            {`Loaded ${loadedNames.length} workbook(s): ` +
                              loadedNames.slice(0, 6).join(", ") +
                              (loadedNames.length > 6 ? `, … (+${loadedNames.length - 6} more)` : "")}
                          </p>
                        )}
                        {unreadable.length > 0 && (
                          <p className="badge badge-risk" style={{ display: "block", margin: "8px 0 0" }}>
                            {`Could not read — NOT included in the reconciliation: ${unreadable.map((m) => m.fname).join(", ")}. ` +
                              "Not a recognised Excel, PDF or CSV file."}
                          </p>
                        )}
                        {fileWarnings.map(([fname, msgs]) => (
                          <p key={fname} className="muted note note-warn">
                            {`${fname} — ${msgs.join("; ")}. The file was included; check it.`}
                          </p>
                        ))}
                      </>
                    )
                  )}
                  {matched.length > 0 && (
                    <>
                      <label className="field-label">Sheets to merge ({matched.length} matched) — untick any picked wrong</label>
                      <div className="sheet-checklist">
                        {matched.map((m) => {
                          const k = mergeKey(m.fname, m.sheet);
                          const on = mergeSel.has(k);
                          return (
                            <label key={k} className="sheet-check">
                              <input type="checkbox" checked={on} onChange={() => {
                                const next = new Set(mergeSel);
                                if (on) next.delete(k); else next.add(k);
                                setMergeSel(next);
                              }} />
                              <span>{m.fname} › {m.sheet}{m.account ? `  (${m.account.replace(/_/g, " ")})` : ""}</span>
                            </label>
                          );
                        })}
                      </div>
                      {preUnticked > 0 && (
                        <p className="muted note">
                          {preUnticked} older month {preUnticked === 1 ? "tab starts" : "tabs start"} unticked
                          — only the latest month is pre-selected. Tick any you also need.
                        </p>
                      )}
                      {supersededSheets.map((m) => (
                        <p key={mergeKey(m.fname, m.sheet)} className="muted note">
                          {`${m.sheet} starts unticked: every item on it is already on ${m.superseded_by}, ` +
                            "so merging both would count them twice. Tick it if you need it."}
                        </p>
                      ))}
                      {mergeSel.size === 0 && (
                        <p className="muted note note-warn">
                          No sheets selected — select at least one to reconcile.
                        </p>
                      )}
                    </>
                  )}
                  {ignored.length > 0 && (
                    <p className="muted note">
                      Ignored (not part of this insurer's layout): {ignored.slice(0, 6).map((m) => `${m.fname} › ${m.sheet}`).join(", ")}
                      {ignored.length > 6 ? `, … (+${ignored.length - 6} more)` : ""}
                    </p>
                  )}
                </>
              ) : (
                <>
                  <FilePicker accept=".xlsx,.xls,.xlsb,.pdf,.csv" required
                    status={insurerFile ? "done" : undefined}
                    label={insurerFile ? "Replace insurer file…" : "Choose or drop insurer file (.xlsx/.xls/.xlsb/.pdf)"}
                    onFiles={(f) => onInsurerFile(f[0])} />
                  {insurerFile && (
                    <FilePreviewCard filename={insurerFile.name} loaded
                      sheet={insurerSheet || undefined} />
                  )}
                  {insurerFile && (
                    <>
                      <label className="field-label" htmlFor="insurer-sheet">Sheet to parse</label>
                      <select id="insurer-sheet" className="select" value={insurerSheet} onChange={(e) => setInsurerSheet(e.target.value === NO_SHEET ? "" : e.target.value)}>
                        <option value="">{NO_SHEET}</option>
                        {insSheets.map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                      {insDetectedSheet && insurerSheet === insDetectedSheet && (
                        <p className="muted note">
                          ✓ Auto-detected sheet <strong>{insDetectedSheet}</strong> from cached layout.
                        </p>
                      )}
                    </>
                  )}
                </>
              )}
              {/* The insurer side's own prior-period open items, beside the SOA they
                  belong to. GL only, and superseded by a previous output file. */}
              {showPriorPeriod && (
                <PriorPeriodPicker
                  side="Insurer"
                  file={unrecFile}
                  onPick={setUnrecFile}
                  onPreviousOutput={(f) => pickPrevFile(f, "insurer")}
                  disabled={!!unrecPrevFile}
                  insurerName={insurerName}
                  sheet={unrecSheet}
                  onSheet={setUnrecSheet}
                />
              )}
            </div>
          </div>

          <div className="card card-stacked">
            <p className="kpi-label kpi-label--left section-label">
              Reconciliation basis
              <InfoTip text="OS (Outstanding Statement) reconciles closing balances. GL (General Ledger) reconciles transaction amounts. The app pre-selects the basis configured for the insurer you picked — change it only if you know this month is the other kind." />
            </p>
            {/* Real radio semantics: role=radio + roving tabindex, so the basis can be
                chosen from the keyboard (it governs the whole run — it must not be
                mouse-only). Arrow keys move between the two options. */}
            <div className="radio-group" role="radiogroup" aria-label="Reconciliation basis">
              {([["OS", "OS — Outstanding Statement (balance basis)"],
                 ["GL", "GL — General Ledger (amount basis)"]] as const).map(([val, text]) => {
                const on = val === "GL" ? modePick === "GL" : modePick !== "GL";
                return (
                  <div key={val} role="radio" aria-checked={on} tabIndex={on ? 0 : -1}
                    className={`radio-pill ${on ? "active" : ""}`}
                    onClick={() => pickMode(val)}
                    onKeyDown={(e) => {
                      if (e.key === " " || e.key === "Enter") { e.preventDefault(); pickMode(val); }
                      else if (e.key === "ArrowRight" || e.key === "ArrowLeft" ||
                               e.key === "ArrowUp" || e.key === "ArrowDown") {
                        e.preventDefault();
                        pickMode(val === "OS" ? "GL" : "OS");
                      }
                    }}>
                    {text}
                  </div>
                );
              })}
            </div>
            {pickedInfo?.cri_basis
              ? <p className="muted note-lg">
                  {modeTouched && modePick !== pickedInfo.cri_basis
                    ? <>Overriding <strong>{insurerName}</strong>&rsquo;s configured basis (<strong>{pickedInfo.cri_basis}</strong>) — your pick governs the run.</>
                    : <>Pre-selected <strong>{pickedInfo.cri_basis}</strong> from <strong>{insurerName}</strong>&rsquo;s configured basis — change it if this month differs.</>}
                </p>
              : mode && <p className="muted note-lg">No basis configured for this insurer — defaulting to <strong>OS</strong>. Your pick governs the run.</p>}
          </div>

          {/* THE ROLL-FORWARD, and the normal path from the second reconciliation
              onward: finance uploads LAST MONTH'S OUTPUT WORKBOOK and the app takes the
              carried items off its Group 1/2/3 tabs. One upload, BOTH books - which is
              why it sits on its own rather than inside either ledger card, and why it
              disables the two per-ledger pickers above (supplying both would state the
              same population twice). GL only; leaving it empty runs exactly as before. */}
          {showPriorPeriod && (
            <div className="card card-stacked">
              <p className="kpi-label kpi-label--left section-label">
                Previous month&rsquo;s reconciliation output
                <span className="badge badge-inline badge-optional">Optional</span>
                <InfoTip text="The Excel file this app produced for the previous period. Its Group 1/2/3 tabs hold everything that was still unreconciled on BOTH books, so this one upload carries the whole prior position forward and each item keeps the age it already had. Those items are listed under Groups 1-3 and drive an Adjusted Closing Balance; your Closing Balance and Difference are not changed." />
              </p>
              <p className="muted" style={{ margin: "0 0 12px" }}>
                Reconciled this insurer before? Upload last month&rsquo;s output file and
                the still-open items from both ledgers are carried forward automatically.
                For a <strong>first</strong> reconciliation, leave this empty and attach
                the open items to each ledger above instead.
              </p>
              <FilePicker
                accept=".xlsx,.xls,.xlsb"
                label={unrecPrevFile ? "Replace previous output file…" : "Choose previous month's output file (optional)"}
                onFiles={(files) => pickPrevFile(files[0] ?? null)}
              />
              {unrecPrevFile
                ? <p className="muted note-lg">
                    {prevMovedFrom && <>Previous month&rsquo;s output detected in the {prevMovedFrom} open-items
                      box, so it was moved here. </>}
                    <strong>{unrecPrevFile.name}</strong> &mdash; both books are read from
                    its Group 1/2/3 tabs, so the per-ledger boxes above are not needed.
                    {" "}
                    <button className="btn btn-tiny"
                      onClick={() => pickPrevFile(null)}>Remove</button>
                  </p>
                : <p className="muted note-lg">
                    Leave empty to reconcile this period on its own.
                  </p>}
            </div>
          )}

          <div style={{ marginTop: 20 }}>
            <button className="btn btn-primary" disabled={!canRun} onClick={startRun}>
              {busy ? "Running…" : "Run Reconciliation"}
            </button>
            {/* The static stage list is a placeholder for the moment before the engine
                reports; once a real stage arrives the live label below supersedes it. */}
            {status && !stage && <span style={{ marginLeft: 12 }} className="muted">{status}</span>}
            {busy && (
              <>
                {/* Determinate once the engine reports a stage; indeterminate until then
                    (and for a cache hit, which finishes before any stage is published). */}
                {stage && stage.total > 0 ? (
                  <div className="progress-track" style={{ marginTop: 10, maxWidth: 520 }}>
                    <div className="progress-fill"
                      style={{ width: `${Math.round((stage.index / stage.total) * 100)}%` }} />
                  </div>
                ) : (
                  <div className="progress-indeterminate" style={{ marginTop: 10, maxWidth: 520 }} />
                )}
                {/* Announce stage changes to screen readers — the visual bar alone
                    conveys nothing to a non-sighted user. polite = don't interrupt. */}
                <p className="muted" style={{ margin: "6px 0 0" }} aria-live="polite">
                  {stage
                    ? <><strong>{stage.stage}</strong>{stage.total > 0 && ` — step ${stage.index} of ${stage.total}`}</>
                    : (isNewInsurer
                      ? "First run for a new insurer can take up to a minute — the layout is being detected."
                      // Not "10-20 seconds": Dubai measured ~75s cold, and an under-promise
                      // reads as a hang and invites a second click.
                      : "A large statement can take a minute or more. The dashboard opens automatically when it's done.")}
                  {elapsed > 0 && ` · ${elapsed}s elapsed`}
                </p>
              </>
            )}
            {runError && !busy && (
              <div ref={runErrorRef} role="alert" className="badge badge-risk"
                style={{ display: "block", margin: "12px 0 24px", maxWidth: 760 }}>
                <strong>Reconciliation did not run</strong>
                <div style={{ marginTop: 4 }}>{runError.lead}</div>
                {runError.detail && (
                  <div style={{ marginTop: 6, fontSize: "0.85em", fontWeight: 400 }}>{runError.detail}</div>
                )}
              </div>
            )}
            {!busy && waitingFor.length > 0 && (
              <p className="muted note-lg">Waiting for: {waitingFor.join(", ")}</p>
            )}
          </div>
          </div>
        </div>
        )}
      </div>
    </div>
  );
}
